import React, { useState, useEffect } from 'react';
import slide1Desktop from "../../assets/landing-slide-1-desktop.jpg";
import slide1Mobile from "../../assets/landing-slide-1-mobile.jpg";
import slide2Desktop from "../../assets/landing-slide-2-desktop.jpg";
import slide2Mobile from "../../assets/landing-slide-2-mobile.jpg";
import slide3Desktop from "../../assets/landing-slide-3-desktop.jpg";
import slide3Mobile from "../../assets/landing-slide-3-mobile.jpg";
import slide4Desktop from "../../assets/landing-slide-4-desktop.jpg";
import slide4Mobile from "../../assets/landing-slide-4-mobile.jpg";
import { Box, Toolbar, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import NavBar from "./navBar/navBar";
import HeroSection from "./sections/heroSection";

// Each scene owns its media as well as the presentation rules that keep the
// navigation and hero copy readable over that particular composition.
const carouselSlides = [
  {
    desktop: slide1Desktop,
    mobile: slide1Mobile,
    hero: {
      titleColor: "#ffffff",
      subtitleColor: "rgba(255, 255, 255, 0.96)",
      textShadow: "0 3px 18px rgba(0, 0, 0, 0.78)",
      desktop: {
        alignItems: "center",
        contentAlign: "center",
        textAlign: "center",
        maxWidth: "880px",
        translateY: "-12px",
      },
      mobile: {
        alignItems: "center",
        contentAlign: "center",
        textAlign: "center",
        maxWidth: "100%",
        translateY: "-48px",
      },
    },
    navbar: {
      foreground: "#ffffff",
      activeColor: "#ffffff",
      underlineColor: "#ffffff",
      borderColor: "rgba(255, 255, 255, 0.42)",
      fieldBorderColor: "rgba(255, 255, 255, 0.62)",
      backgroundColor: "rgba(20, 12, 10, 0.12)",
      logoFilter: "brightness(0) invert(1)",
      textShadow: "0 1px 8px rgba(0, 0, 0, 0.55)",
    },
  },
  {
    desktop: slide2Desktop,
    mobile: slide2Mobile,
    hero: {
      copyKey: "management",
      ctaKey: "find_establishment",
      desktop: {
        position: "absolute",
        top: "68px",
        left: "45%",
        contentAlign: "flex-start",
        textAlign: "left",
        maxWidth: "min(270px, 23vw)",
        titleFontSize: "clamp(1.05rem, 1.45vw, 1.25rem)",
        titleMarginBottom: "18px",
        subtitleFontSize: "clamp(0.74rem, 0.88vw, 0.84rem)",
        subtitleColor: "#1f2937",
        subtitleFontWeight: 600,
        subtitleLineHeight: 1.45,
        subtitleBackground: "rgba(255, 255, 255, 0.84)",
        subtitlePadding: "8px 10px",
        subtitleBorderRadius: "10px",
        subtitleMarginBottom: "22px",
        buttonPadding: "11px 18px",
        buttonFontSize: "0.82rem",
      },
      mobile: {
        alignItems: "center",
        contentAlign: "center",
        textAlign: "center",
        maxWidth: "100%",
        translateY: "-50px",
      },
    },
    navbar: {},
  },
  {
    desktop: slide3Desktop,
    mobile: slide3Mobile,
    hero: {
      copyKey: "time",
      ctaKey: "find_establishment",
      desktop: {
        position: "absolute",
        top: "44px",
        left: "6.8%",
        contentAlign: "flex-start",
        textAlign: "left",
        maxWidth: "650px",
        titleFontSize: "clamp(2.15rem, 3vw, 3rem)",
        titleColor: "#ffffff",
        titleMarginBottom: "10px",
        subtitleFontSize: "clamp(1rem, 1.2vw, 1.15rem)",
        subtitleMaxWidth: "540px",
        subtitleColor: "#ffffff",
        subtitleFontWeight: 600,
        subtitleLineHeight: 1.45,
        subtitleMarginBottom: "150px",
        textShadow: "0 2px 10px rgba(0, 0, 0, 0.65)",
      },
      mobile: {
        alignItems: "center",
        contentAlign: "center",
        textAlign: "center",
        maxWidth: "100%",
        translateY: "-88px",
      },
    },
    navbar: {},
  },
  {
    desktop: slide4Desktop,
    mobile: slide4Mobile,
    hero: {
      copyKey: "mobility",
      ctaKey: "find_establishment",
      titleColor: "#ffffff",
      subtitleColor: "rgba(255, 255, 255, 0.96)",
      textShadow: "0 3px 18px rgba(0, 0, 0, 0.76)",
      desktop: {
        alignItems: "flex-end",
        contentAlign: "flex-end",
        textAlign: "right",
        maxWidth: "560px",
        translateY: "-62px",
      },
      mobile: {
        alignItems: "center",
        contentAlign: "center",
        textAlign: "center",
        maxWidth: "100%",
        translateY: "-72px",
        titleColor: "#a66941",
        subtitleColor: "#34445a",
        textShadow: "0 2px 10px rgba(255, 255, 255, 0.88)",
      },
    },
    navbar: {},
  },
];

const Dashboard = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-advance 10 seconds after the latest automatic or manual change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentImageIndex((prevIndex) =>
        (prevIndex + 1) % carouselSlides.length
      );
    }, 10000);

    return () => clearTimeout(timeout);
  }, [currentImageIndex]);

  // Manual navigation functions
  const goToPrevious = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? carouselSlides.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prevIndex) =>
      (prevIndex + 1) % carouselSlides.length
    );
  };

  // Carousel container style
  const carouselContainerStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    zIndex: -1,
  };

  // Carousel track style that slides
  const carouselTrackStyle = {
    display: "flex",
    width: `${carouselSlides.length * 100}%`,
    height: "100%",
    transform: `translateX(-${currentImageIndex * (100 / carouselSlides.length)}%)`,
    transition: "transform 0.8s ease-in-out",
  };

  // Individual slide style
  const slideStyle = {
    width: `${100 / carouselSlides.length}%`,
    height: "100%",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "scroll",
    minHeight: "100vh",
  };

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflowX: 'hidden'
      }}
    >
      {/* Sliding Carousel Container */}
      <Box sx={carouselContainerStyle}>
        <Box sx={carouselTrackStyle}>
          {carouselSlides.map((slide) => (
            <Box
              key={slide.desktop}
              sx={{
                ...slideStyle,
                backgroundImage: {
                  xs: `url(${slide.mobile})`,
                  md: `url(${slide.desktop})`,
                },
              }}
            />
          ))}
        </Box>
      </Box>
      {/* Navigation Arrows */}
      <IconButton
        onClick={goToPrevious}
        aria-label="Image précédente"
        sx={{
          position: "absolute",
          left: { xs: 4, sm: 8, md: 20 },
          top: "50%",
          transform: "translateY(-50%)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: "white",
          zIndex: 1000,
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>

      <IconButton
        onClick={goToNext}
        aria-label="Image suivante"
        sx={{
          position: "absolute",
          right: { xs: 4, sm: 8, md: 20 },
          top: "50%",
          transform: "translateY(-50%)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: "white",
          zIndex: 1000,
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <ChevronRight fontSize="small" />
      </IconButton>

      {/* Dots Indicator */}
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 1,
          zIndex: 1000,
        }}
      >
        {carouselSlides.map((_, index) => (
          <Box
            key={index}
            component="button"
            type="button"
            aria-label={`Afficher l'image ${index + 1}`}
            onClick={() => setCurrentImageIndex(index)}
            sx={{
              width: 12,
              height: 12,
              minWidth: 12,
              padding: 0,
              border: 0,
              borderRadius: "50%",
              backgroundColor: index === currentImageIndex ? "white" : "rgba(255, 255, 255, 0.5)",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              "&:hover": {
                backgroundColor: "white",
              },
            }}
          />
        ))}
      </Box>

      <NavBar appearance={carouselSlides[currentImageIndex].navbar} />

      <Box
        component="main"
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Toolbar />
        <HeroSection presentation={carouselSlides[currentImageIndex].hero} />
      </Box>
    </Box>
  );
};

export default Dashboard;
